import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { Waypoint } from 'react-waypoint';
import { Box, xcss } from '@atlaskit/primitives';
import { LoadingState, TimeGroup } from '@atlassian/notification-types/types';
import { DocumentSkeleton } from '../../common/ui/document';
import { ExperienceMark, NotificationExperienceMarkKeys } from '../../common/utils/experience-tracking/adapter';
import messages from '../../common/utils/i18n/messages';
import { ListFocusProvider } from '../../common/utils/useFocusManager';
import { BlankSkeletonPlaceholder, FeedSection, GroupHeadingWrapper, SectionWrapper, TimeGroupHeading } from './styled';
import { calculateAboveGroup, calculateBelowGroup, resolveTimeGroupMessage } from './utils';
var styles = xcss({
  listStyle: 'none'
});
function useBatchedRenderingLogic() {
  /**
   * This is very basic optimisation for tab switching
   * to avoid rendering hundreds of items, potentially with ADF when you tab switch.
   * Instead it will render a few, and then after 100ms, it will render the rest.
   * This doesn't take into account height of the window - all hardcoded.
   */
  var _useState = useState(8),
    _useState2 = _slicedToArray(_useState, 2),
    renderLimit = _useState2[0],
    setRenderLimit = _useState2[1];
  var _useState3 = useState(true),
    _useState4 = _slicedToArray(_useState3, 2),
    forceContentLoading = _useState4[0],
    setForceContentLoading = _useState4[1];
  useEffect(function () {
    var renderLimitTimeout = null;
    var contentLoadingTimeout = setTimeout(function () {
      setForceContentLoading(false);
      renderLimitTimeout = setTimeout(function () {
        setRenderLimit(null);
      }, 150);
    }, 150);
    return function () {
      if (renderLimitTimeout !== null) {
        clearTimeout(renderLimitTimeout);
      }
      clearTimeout(contentLoadingTimeout);
    };
  }, []);
  return {
    forceContentLoading: forceContentLoading,
    renderLimit: renderLimit
  };
}
export function NotificationFeed(_ref) {
  var updateGroupingHeaders = _ref.updateGroupingHeaders,
    notificationTimeGroups = _ref.notificationTimeGroups,
    fetchState = _ref.fetchState,
    _ref$hideFirstGroupin = _ref.hideFirstGroupingHeader,
    hideFirstGroupingHeader = _ref$hideFirstGroupin === void 0 ? false : _ref$hideFirstGroupin,
    testId = _ref.testId,
    renderDataItem = _ref.renderDataItem,
    product = _ref.product,
    ThirdPartyNotificationNudge = _ref.ThirdPartyNotificationNudge;
  var intl = useIntl();
  var _useBatchedRenderingL = useBatchedRenderingLogic(),
    renderLimit = _useBatchedRenderingL.renderLimit,
    forceContentLoading = _useBatchedRenderingL.forceContentLoading;
  var listIndex = 0;
  var skeletonsRenderCounter = 0;
  var onlyOlderTimeGroup = notificationTimeGroups.length === 1 && notificationTimeGroups[0].timeGroup === TimeGroup.OLDER;

  // Enter from above to inside is fired when scrolling up from a current group to a new group
  var handleNewGroupAbove = function handleNewGroupAbove(previousPosition, currentPosition, index, timeGroups) {
    var timeGroupMessage = calculateAboveGroup(previousPosition, currentPosition, index, timeGroups, onlyOlderTimeGroup);
    if (timeGroupMessage !== undefined) {
      updateGroupingHeaders(timeGroupMessage);
    }
    return timeGroupMessage;
  };

  // Leave from inside to above is fired when scrolling down into a new group
  var handleNewGroupBelow = function handleNewGroupBelow(previousPosition, currentPosition, index, timeGroups) {
    var timeGroupMessage = calculateBelowGroup(previousPosition, currentPosition, index, timeGroups, onlyOlderTimeGroup);
    if (timeGroupMessage !== undefined) {
      updateGroupingHeaders(timeGroupMessage);
    }
    return timeGroupMessage;
  };
  return /*#__PURE__*/React.createElement(FeedSection, {
    "data-testid": testId,
    "aria-label": intl.formatMessage(messages.allyListDescription),
    role: "feed",
    "aria-busy": fetchState === LoadingState.LOADING
  }, /*#__PURE__*/React.createElement(ListFocusProvider, null, (product === 'jira' || product === 'confluence') && ThirdPartyNotificationNudge !== undefined && /*#__PURE__*/React.createElement(ThirdPartyNotificationNudge, null), notificationTimeGroups.map(function (notificationTimeGroup, index) {
    return /*#__PURE__*/React.createElement(SectionWrapper, {
      key: "time-group-".concat(notificationTimeGroup.timeGroup)
    }, (!hideFirstGroupingHeader || index > 0) && /*#__PURE__*/React.createElement(GroupHeadingWrapper, null, /*#__PURE__*/React.createElement(TimeGroupHeading, {
      "data-testid": "time-group-heading-".concat(notificationTimeGroup.timeGroup)
    }, /*#__PURE__*/React.createElement(FormattedMessage, resolveTimeGroupMessage(notificationTimeGroup.timeGroup, onlyOlderTimeGroup)))), /*#__PURE__*/React.createElement(Waypoint, {
      onEnter: function onEnter(_ref2) {
        var previousPosition = _ref2.previousPosition,
          currentPosition = _ref2.currentPosition;
        return handleNewGroupAbove(previousPosition, currentPosition, index, notificationTimeGroups);
      },
      onLeave: function onLeave(_ref3) {
        var previousPosition = _ref3.previousPosition,
          currentPosition = _ref3.currentPosition;
        return handleNewGroupBelow(previousPosition, currentPosition, index, notificationTimeGroups);
      },
      topOffset: "50px"
    }), /*#__PURE__*/React.createElement(Box, {
      as: "ul",
      padding: "space.0"
    }, notificationTimeGroup.data.map(function (dataItem) {
      if (renderLimit !== null && listIndex + 1 > renderLimit) {
        skeletonsRenderCounter += 1;
        if (skeletonsRenderCounter <= 8) {
          return /*#__PURE__*/React.createElement(DocumentSkeleton, {
            key: dataItem.id,
            testId: "notification-item-opt-skeleton"
          });
        }
        return /*#__PURE__*/React.createElement(BlankSkeletonPlaceholder, {
          key: dataItem.id,
          "data-testid": "notification-item-opt-skeleton"
        });
      }
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: dataItem.id
      }, /*#__PURE__*/React.createElement(Box, {
        as: "li",
        xcss: styles
      }, renderDataItem(dataItem, listIndex++, forceContentLoading)));
    })));
  }), /*#__PURE__*/React.createElement(ExperienceMark, {
    mark: NotificationExperienceMarkKeys.NOTIFICATIONS_FEED_SKELETON_MOUNT,
    phase: "end"
  })));
}